import uiConfig from 'src/lib/ui/config';
import {
  ImageCoverView,
  ImageScaleView,
  ImageView,
  View,
  animate,
} from '@play-co/timestep-core/ui';
import ResponsiveScene from './ResponsiveScene';
import StateObserver, { user } from 'src/StateObserver';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { PetEntity } from '../logic/EntityManager';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { getPet } from '../pet';
import { GameScene } from '../minigame/match2-timestep/game/GameScene';
import { Container } from '../minigame/match2-timestep/odie';
import { BlockEntity } from '../minigame/match2-timestep/game/entities/BlockEntity';
import { createWaiter, animDuration } from 'src/lib/utils';
import { Match2LevelPanel } from '../minigame/match2-timestep/components/Match2LevelPanel';
import { Match2Highlight } from '../minigame/match2-timestep/components/Match2Highlight';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import sounds from 'src/lib/sounds';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { GameEvent } from '../minigame/match2-timestep/game/defs/event';
import { Match2BoosterInfo } from '../minigame/match2-timestep/components/Match2BoosterInfo';
import {
  BoosterId,
  boosterIds,
} from '../minigame/match2-timestep/defs/booster';
import { Match2Tip } from '../minigame/match2-timestep/components/Match2Tip';
import { BlockId } from '../minigame/match2-timestep/game/defs/block';
import { TipId } from '../minigame/match2-timestep/game/defs/tips';
import {
  getMatch2Level,
  getMatch2UnlockableLevels,
} from 'src/replicant/match2/match2.getters';
import {
  clearMatch2GameplayState,
  loadMatch2GameplayState,
} from '../minigame/match2-timestep/savestate/match2-savestate.utils';
import { resetUnlimitedBoostersUsageInSession } from '../minigame/match2-timestep/match2.util';
import { Match2PowerupType } from 'src/replicant/match2/match2.ruleset';
import { mapToViewPosition } from '../minigame/match2-timestep/game/util/mapTools';
import { config } from '../minigame/match2-timestep/game/defs/config';
import { Match2MovesPanelNew } from '../minigame/match2-timestep/components/Match2MovesPanelNew';
import { Match2GoalsPanelNew } from '../minigame/match2-timestep/components/Match2GoalsPanelNew';
import { loadMatch2LevelAssets } from 'src/loadMatch2LevelAssets';
import { matchTheme, ParticleEffect } from '../theme/match2/Match2Theme';
import { Match2BoostersBarNew } from '../minigame/match2-timestep/components/Match2BoostersBarNew';
import NakedPromise from '../minigame/match2-timestep/lib/pattern/NakedPromise';
import { puzzleCoinsToSAndTip } from '../puzzlecoins/puzzlecoins.sequence';
import { createUIPetCrate } from '../pet/pet.utils';
import { puzzleCoinsIsPlayingTutorialDeterministic } from 'src/replicant/puzzlecoins/puzzlecoins.getters';
import { downscale } from 'src/conf/scale';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';

let goals: Match2GoalsPanelNew | undefined = undefined;
const LAST_MOVES = 6; // decides when to show "5 Moves Left" Banner

export function getGoals() {
  return goals;
}

function setGoals(value?: typeof goals) {
  goals = value;
}

// The base Y position of the game container
const GAME_CONTAINER_BASE_Y = 680;

/** Configuration options for a Match-2 puzzle scene. */
export type Match2SceneOptions = {
  /** Background image rendered behind the regular puzzle UI. */
  backgroundImage?: string;
  /** Whether standard Match-2 loss recovery callouts and retries are enabled. */
  enableLoseRecovery?: boolean;
  /** Specialized warning displayed instead of standard quit-loss callouts. */
  quitWarningMessage?: string;
};

/** Regular Match-2 puzzle scene with the standard HUD and gameplay runtime. */
export default class Match2Scene extends ResponsiveScene {
  private platform: View;
  protected pet: PetEntity;
  private species: PetSpeciesID | undefined;
  private state!: 'idle' | 'play' | 'joy' | undefined;
  private gameStage?: Container;
  protected game?: GameScene;
  protected goals: Match2GoalsPanelNew;
  private level: Match2LevelPanel | undefined;
  protected moves: Match2MovesPanelNew;
  private boosters: Match2BoostersBarNew;
  protected quit: ButtonScaleView;
  private animationTimer = 0;
  private highlight?: Match2Highlight;
  private boosterInfo?: Match2BoosterInfo;
  private tip?: Match2Tip;
  public levelNumber!: number;
  public selectedPowerups: Match2PowerupType[] = [];
  public bonusPowerups: Match2PowerupType[] = [];
  private gameContainer: View;
  private particleEffect?: ParticleEffect;
  /** Promise resolved after the Match-2 runtime has finished starting. */
  protected startGameReady?: Promise<void>;
  private hasFiveMovesDialogShown = false;
  /** Whether this puzzle scene uses the standard Match-2 loss recovery flow. */
  private readonly enableLoseRecovery: boolean;

  public getIcon(icon: string): View | undefined {
    if (icon === 'goals') return this.goals;
    return undefined;
  }

  /**
   * Create a Match-2 puzzle scene.
   *
   * @param options Optional presentation overrides for specialized puzzle scenes.
   */
  constructor(options: Match2SceneOptions = {}) {
    const theme = matchTheme();
    super(
      new ImageCoverView({
        ...uiConfig.buruburu.backgroundDefaults,
        image: options.backgroundImage ?? theme.getLevelBackground(),
      }),
    );

    // Standard Match-2 scenes retain the complete loss recovery flow by default.
    this.enableLoseRecovery = options.enableLoseRecovery ?? true;

    const view = this.getView();
    const { width } = view.style;
    const platform = new ImageView({
      width: 589,
      height: 294,
      centerOnOrigin: true,
      x: width / 2,
      zIndex: -2, // Match2 game zIndex is -1, so this should go behind it
      ...theme.getPetContainerStyle(),
    });

    this.platform = view.addSubview(platform);

    this.species = Pet.getters.getEquippedPetSpecies(
      StateObserver.getState().user,
    );
    this.pet = new PetEntity('minigame', this.species);

    const platformStyle = this.platform.style;
    // Center the pet's rendered body above the score progress bar.
    this.pet.updateOpts({
      superview: this.platform,
      x: platformStyle.width * 0.5 + 60,
      y: 125,
      scale: 0.7,
    });

    // Set up a container for the game - this container will be moved with the resize.
    this.gameContainer = new View({
      superview: view,
      x: this.getView().style.width / 2,
      y: 400,
      zIndex: -1,
      infinite: true,
    });

    this.gameStage = new Container();
    this.gameStage.tag = 'GameStage';
    this.gameContainer.addSubview(this.gameStage.view);

    this.goals = theme.getGoalsPanel({ superview: view });
    this.level = theme.getLevelPanel({
      parent: this.goals,
      x: this.goals.style.width * 0.5,
      y: this.goals.style.height + 22,
    });

    this.moves = theme.getMovesPanel({
      parent: view,
      x: width - this.goals.style.x,
      y: this.goals.style.y,
    });

    this.boosters = theme.getBoosterBar({
      parent: view,
      x: 40,
    });

    this.quit = new ButtonScaleView({
      parent: view,
      image: 'assets/ui/match2/common/button_puzzle_quit.png',
      width: 101,
      height: 110,
      scaleMethod: 'contain',
      x: this.boosters.style.x + this.boosters.style.width + 101 * 0.5 + 15,
      centerOnOrigin: true,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.QUIT.BUTTON,
      },
      onDown: () => {
        animate(this.quit).clear().then({ scale: 0.9 }, 50);
      },
      onUp: () => {
        animate(this.quit).clear().then({ scale: 1 }, 200);
      },
      onClick: async () => {
        sounds.playSound('ok');

        const success = await openPopupPromise('popupMatch2Quit', {
          objectivesCount: this.game?.getFrenzyObjectivesCount() ?? 0,
          showLoseRecovery: this.enableLoseRecovery,
          warningMessage: options.quitWarningMessage,
        });
        success && this.game?.finish();
      },
      ...theme.getQuitButtonStyle(),
    });

    setGoals(this.goals);

    this.applyThemeParticleEffect();
  }

  private applyThemeParticleEffect() {
    this.particleEffect = matchTheme().getParticleEffect({
      superview: this.getView(),
      width: this.getView().style.width,
      height: 450,
      zIndex: -100,
    });
  }

  public showScene() {
    const level = this.levelNumber;

    if (puzzleCoinsIsPlayingTutorialDeterministic(user())) {
      // Hide the quit button if the user is playing the tutorial levels
      this.quit.hide();
    } else {
      this.quit.show();
    }

    this.species = getPet().species;
    if (this.species) {
      this.pet.setSpecies(this.species);
    } else {
      // Spawn the crate animation if the user doesn't have a pet yet
      this.spawnCrateAnimation();
    }

    this.animatePet('idle');

    console.log('Starting level', level);

    this.game = new GameScene({
      stage: this.gameStage!,
      enableLoseRecovery: this.enableLoseRecovery,
    });

    this.game.startingBoosters = this.selectedPowerups;

    // Queue pre-selected powerups
    this.game.powerBlockSystem.queueExtraPowerups(this.selectedPowerups);

    // Queue free Buru streak powerups
    this.game.powerBlockSystem.queueExtraPowerups(this.bonusPowerups, true);

    const { width, height } = this.getView().style;

    this.game.resize(width, height);
    this.startGameReady = this.game
      .startGame(level, loadMatch2GameplayState())
      .then(() => {
        this.goals.setGoals(this.game!.sessionEntity.c.map.goals);
        this.moves.setMoves(this.game!.moves);
      });

    if (this.moves instanceof Match2MovesPanelNew) {
      this.moves.setLevel(level);
    } else if (this.level instanceof Match2LevelPanel) {
      this.level.setLevel(level);
    }
    resetUnlimitedBoostersUsageInSession();
    this.boosters.refresh();

    this.game.events.subscribe(this.handleGameEvents.bind(this));

    // Give some extra time for the scene transition to complete
    setTimeout(() => this.triggerTips(), 300);

    this.particleEffect?.start();
  }

  protected async triggerTips() {
    // Ensure the game has finished starting before running animations/tips
    await this.startGameReady;

    // Show the match2 tutorial just before the block tips, if suitable
    if (this.levelNumber === 1) {
      await puzzleCoinsToSAndTip();
      return;
    }

    // Block tips takes priority, skip booster tips if block tips are available
    if (this.tipBlock()) return;
    this.tipBooster();
  }

  public hideScene() {
    // Stop boosters unlimited state update loop
    this.boosters.stopAutoUpdate();

    // Fully destroy the game to free up memory
    this.game?.destroy();

    // Ensure that game and game stage are eligible for garbage collection
    this.game = undefined;
    this.gameStage = undefined;

    setGoals();

    this.particleEffect?.stop();
  }

  private updateAnimationTimer() {
    if (!this.animationTimer) return;
    this.animationTimer -= 100;
    if (this.animationTimer <= 0) {
      this.animatePet('idle');
      this.animationTimer = 0;
    }
    createWaiter().wait(() => this.updateAnimationTimer(), 100);
  }

  private handleGameEvents(e?: GameEvent) {
    if (!e || !this.game) return;

    if (e.id === 'finish') {
      // Fully block user input when game is finished
      this.getView().updateOpts({
        blockEvents: true,
      });
    }

    if (e.id === 'input') {
      // Clear any existing save state on any tap interaction
      clearMatch2GameplayState();
    }

    if (e.id === 'move' || e.id === 'continue' || e.id === 'updateHud') {
      if (this.game.moves === LAST_MOVES) {
        this.showFivesMovesLeftDialog();
      }
      this.moves.setMoves(
        this.game.moves - this.game.visualMoves - (e.id === 'move' ? 1 : 0),
      );
    }

    if (e.id === 'goal' || e.id === 'ungoal') {
      this.goals.setGoals(this.game.sessionEntity.c.map.goals);
    }

    if (e.id === 'booster') {
      if (e.subtype === 'run') this.boosters.setInteractive(false);
      if (e.subtype === 'finish') this.boosters.setInteractive(true);
      if (e.subtype === 'activate') {
        this.highlightBooster(e.boosterId, e.handPosition);
      }
      if (e.subtype === 'run' || e.subtype === 'cancel') {
        this.highlighStop();
      }
    }

    const useAnimation = e.id === 'power' || e.id === 'destroy';

    if (useAnimation) {
      this.animatePet(e.id === 'power' ? 'joy' : 'play');
      const isAlreadyAnimating = this.animationTimer > 0;

      // Reset timer to extend animation duration
      this.animationTimer = 1000;

      // Start waiter loop if not already running
      if (!isAlreadyAnimating) this.updateAnimationTimer();
    }
  }

  public override resize() {
    super.resize();
    const { height } = StateObserver.getState().ui.screenSize;
    this.platform.style.y =
      400 +
      (height - uiConfig.height) * 0.5 +
      matchTheme().getPetContainerOffset();
    if (this.gameStage) {
      this.gameContainer.style.y =
        GAME_CONTAINER_BASE_Y +
        (height - uiConfig.height) * 0.55 +
        matchTheme().getGameYOffset();
    }
    this.boosters.style.y = height - 95;
    this.quit.style.y = this.boosters.style.y;
    this.highlight?.resize({ height });
    this.tip?.resize({ height });
  }

  private animatePet(state: 'idle' | 'play' | 'joy') {
    if (!this.species || this.state === state) return;
    if (state === 'play' && this.state === 'joy') return;
    this.state = state;
    const petAnimations = petAnimationNames[this.species];

    switch (state) {
      case 'idle':
        this.pet.setAnimation(petAnimations.normal.idle);
        break;
      case 'play':
        this.pet.setAnimation(petAnimations.play);
        break;
      case 'joy':
        this.pet.setAnimation(petAnimations.joy);
    }
  }

  public async preload() {
    await loadMatch2LevelAssets();
  }

  private highlightView(view: View) {
    if (!this.highlight) {
      const view = this.getView();
      const { width, height } = view.style;
      this.highlight = new Match2Highlight({
        superview: view,
        width,
        height,
      });
    }
    this.highlight.highlighView(view);
  }

  private async highlighStop() {
    this.highlight?.stopHighlight();
    this.boosterInfo?.hideBooster();
    await this.tip?.hideTip();
  }

  private highlightBooster(
    id: BoosterId,
    handPosition?: { x: number; y: number },
  ) {
    if (!this.game) return;
    this.highlightView(this.game.stage.view);
    if (!this.boosterInfo) {
      const view = this.getView();
      const { width } = view.style;
      this.boosterInfo = new Match2BoosterInfo({
        superview: view,
        x: width * 0.5 - 300,
        y: 100,
      });
    }

    if (handPosition) {
      let { x, y } = mapToViewPosition(handPosition, true);
      const size = config.tile.size;
      const offset = this.game.stage.view.style;

      // Convert map position to view position
      handPosition.x = x + offset.x + size * 0.5;
      handPosition.y = y + offset.y + size * 0.5;
    }

    this.boosterInfo.showBooster(id, handPosition);
    const button = this.boosters.getButtonById(id);
    if (button) this.highlightView(button);
  }

  private async highlightBlock(args: {
    blockId: BlockId;
    tipId: TipId;
    view: View;
  }) {
    const promise = new NakedPromise();
    this.highlightView(args.view);
    if (!this.tip) {
      this.tip = new Match2Tip({
        superview: this.getView(),
      });
    }
    const { width, height } = this.getView().style;
    this.tip.onOk = () => {
      this.highlighStop().then(() => {
        promise.resolve();
      });
    };
    this.tip.updateOpts({ width, height });
    this.tip.showTip(args.tipId, {
      x: args.view.style.x,
      y: args.view.style.y,
    });
    return promise;
  }

  private tipBlock() {
    if (!this.game) return false;
    const tips = this.game.findBlocksWithTips();

    // Skip if there are no tips available in current level
    if (!tips.length) return false;

    // Filter out tips that have been shown already
    const selected = tips.filter((tip) => {
      return !StateObserver.getState().user.match2.tips[tip.tipId];
    });

    // Skip if all tips have been shown already
    if (!selected.length) return false;

    // Show all tips that have not been shown already, in sequence
    void this.showBlockTips(selected);

    return true;
  }

  private async showBlockTips(
    selected: {
      blockId: BlockId;
      tipId: string;
      view: View;
    }[],
  ) {
    for (const tip of selected) {
      // Show the tip and mark it as shown
      void StateObserver.invoke.tipComplete({ id: tip.tipId });
      await this.highlightBlock(tip);
    }
  }

  private tipBooster() {
    // Tips for boosters are a bit different from blocks, they can use their own UI as its tip
    const { user } = StateObserver.getState();
    const level = getMatch2Level(user);
    // Loop through all boosters and find the first one that is unlocked from the current level
    for (const boosterId of boosterIds) {
      // Check if boosterId unlock level matches current level
      if (level === getMatch2UnlockableLevels(boosterId)) {
        // Check if this tip has been shown already
        const alreadyTipped =
          StateObserver.getState().user.match2.tips[boosterId];
        const button = this.boosters.getButtonById(boosterId);
        const amount = user.match2.boosters[boosterId];
        // Trigger the booster button that will serve as a tip
        if (amount > 0 && button && !alreadyTipped) {
          void StateObserver.invoke.tipComplete({ id: boosterId });
          button.toggleBooster(true);
          button.refresh();
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Crate animation to be used in place of pet when pet is not available
   */
  private spawnCrateAnimation() {
    const petCrate = createUIPetCrate();
    petCrate.updateOpts({
      superview: this.platform,
      x: this.platform.style.width * 0.5 + 20,
      y: -60,
      scale: 0.7,
      centerOnOrigin: true,
      zIndex: 1,
    });
  }

  /**
   * Tip a cell by position
   */
  public async tipCells(args: {
    tipId: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }) {
    if (!this.game) return;

    let midView: View;
    for (let x = 0; x < args.width; ++x) {
      for (let y = 0; y < args.height; ++y) {
        const cell = this.game.sessionEntity.c.map.getCellAt({
          x: args.x + x,
          y: args.y + y,
        });
        const base = cell?.base?.entity;
        if (!base) continue;
        const view = base.c.view2d.view.view;
        if (
          x === Math.floor(args.width / 2) &&
          y === Math.floor(args.height / 2)
        ) {
          midView = view;
        }
        this.highlightView(view);
      }
    }

    const promise = new NakedPromise();

    if (!this.tip) {
      this.tip = new Match2Tip({
        superview: this.getView(),
      });
    }
    const { width, height } = this.getView().style;
    this.tip.onOk = () => {
      this.highlighStop().then(() => {
        promise.resolve();
      });
    };
    this.tip.updateOpts({ width, height });
    this.tip.showTip(args.tipId, {
      x: midView!.style.x,
      y: midView!.style.y - 50,
    });
    return promise;
  }

  /**
   * Highlight whole scene and wait for player move
   */
  public async waitForTap() {
    if (!this.game) return;

    // Highlight the pet (or pet crate) on level 1
    if (this.levelNumber === 1) {
      this.highlightView(this.platform);
    }

    // Highlight the game container
    this.highlightView(this.getGameHighlightView());

    // Create a promise that resolves when the player makes a move
    const promise = new NakedPromise();

    // Should clean up event subscription to prevent memory leaks
    const eventHandler = (e?: GameEvent) => {
      if (e?.id === 'move') {
        this.game?.events.unsubscribe(eventHandler);
        this.highlighStop().then(() => promise.resolve());
      }
    };

    // Listen for player move event
    this.game.events.subscribe(eventHandler);

    return promise;
  }

  /**
   * Get move counter view
   *
   * @returns The moves counter view
   */
  public getMovesView(): Match2MovesPanelNew {
    return this.moves;
  }

  /**
   * Get puzzle grid view
   *
   * @returns The puzzle grid view and score progress bar
   */
  public getGameContainerView(): View {
    return this.gameContainer;
  }

  /**
   * Get puzzle view for highlighting
   */
  public getGameHighlightView(): View {
    return this.gameStage!.view;
  }

  /**
   * Highlight the moves view for tutorial
   *
   * @returns A function to stop the highlight
   */
  public highlightMovesView(): () => Promise<void> {
    // Highlight the pet (or pet crate) on level 1
    if (this.levelNumber === 1) {
      this.highlightView(this.platform);
    }

    this.highlightView(this.moves);
    return () => this.highlighStop();
  }

  /**
   * Display the "5 Moves Left" dialog
   */
  public async showFivesMovesLeftDialog() {
    // Show only once per puzzle
    if (this.hasFiveMovesDialogShown) return;
    this.hasFiveMovesDialogShown = true;

    const view = this.getView();

    const fiveMovesLeftWarning = new ImageScaleView({
      parent: view,
      image: 'assets/ui/match2/common/red_speech_bubble.png',
      width: downscale(273),
      height: downscale(190),
      scaleMethod: 'contain',
      centerOnOrigin: true,
      x: view.style.width * 0.5,
      y: 80,
    });

    const fiveMovesLeftText = new LangBitmapFontTextView({
      parent: fiveMovesLeftWarning,
      size: 28,
      font: 'Body',
      color: '#FFFFFF',
      centerOnOrigin: true,
      localeText: () => i18n('minigame.match2.fiveMovesRemain'),
      width: fiveMovesLeftWarning.style.width,
      height: fiveMovesLeftWarning.style.height,
      x: fiveMovesLeftWarning.style.width * 0.5,
      y: fiveMovesLeftWarning.style.height * 0.5 - 12,
      align: 'center',
      verticalAlign: 'center',
      leading: -5,
    });

    // Play pet speak sound
    sounds.playSound('speak');

    // Animate it showing up
    await animate(fiveMovesLeftWarning)
      .clear()
      .now({ scale: 0, opacity: 0 })
      .then({ scale: 1, opacity: 1 }, 500, animate.easeOutBack)
      .wait(animDuration * 10)
      .then({ scale: 0, opacity: 0 }, 500, animate.easeInBack)
      .toPromise();

    // Clean up
    fiveMovesLeftText.removeFromSuperview();
    fiveMovesLeftWarning.removeFromSuperview();
  }
}
